import {
	TRACKER_FIND_PERSON_STALE_SEARCH_HOURS,
	TrackerFindPersonStatus,
} from "@signalist/shared-types";

import { ListSourceType } from "@/enum/list.enum";

import { ListPerson } from "../../schema/list";

/**
 * What a tracker list's lead row says about the automatic person search for its company
 * (PREC-4399). The search records its state on the tracker company, which the row carries through
 * its signal (`trackerSignalResult.trackerOrganization`).
 */
export type TrackerFindPersonRowState =
	| { kind: "SEARCHING"; since: Date }
	| { kind: "NOT_FOUND"; searchedAt: Date | null }
	| { kind: "NO_CREDITS"; at: Date | null }
	| { kind: "OFF" }
	| { kind: "NONE" };

interface TrackerOrganizationFindPerson {
	findPersonStatus?: TrackerFindPersonStatus | null;
	findPersonAttemptedAt?: string | null;
	findPersonProspectId?: number | null;
}

const trackerOrganizationOf = (row: ListPerson): TrackerOrganizationFindPerson | undefined =>
	row.trackerSignalResult?.trackerOrganization as TrackerOrganizationFindPerson | undefined;

const dateOf = (value: string | null | undefined): Date | null => (value ? new Date(value) : null);

const STALE_SEARCH_MS = TRACKER_FIND_PERSON_STALE_SEARCH_HOURS * 60 * 60 * 1000;

/** A search marked as running that started within the bound; an older one died with its worker. */
function runningSince(org: TrackerOrganizationFindPerson | undefined, now: Date): Date | null {
	const since = dateOf(org?.findPersonAttemptedAt);

	return org?.findPersonStatus === TrackerFindPersonStatus.SEARCHING &&
		since &&
		now.getTime() - since.getTime() < STALE_SEARCH_MS
		? since
		: null;
}

/**
 * The state of a placeholder row: the search runs, found nobody, could not be paid for, or (with
 * the list's switch off) is not used. A placeholder of a list with the switch on that was never
 * searched, or whose search died, says nothing more than "Select prospect".
 */
export function trackerFindPersonRowState(
	row: ListPerson,
	findPersonOnSignal: boolean,
	now: Date = new Date()
): TrackerFindPersonRowState {
	const org = trackerOrganizationOf(row);
	const since = runningSince(org, now);

	if (since) {
		return { kind: "SEARCHING", since };
	}

	// With the switch off nothing searches again, so no earlier result may promise it.
	if (!findPersonOnSignal) {
		return { kind: "OFF" };
	}

	if (org?.findPersonStatus === TrackerFindPersonStatus.NOT_FOUND) {
		return { kind: "NOT_FOUND", searchedAt: dateOf(org.findPersonAttemptedAt) };
	}

	if (org?.findPersonStatus === TrackerFindPersonStatus.NO_CREDITS) {
		return { kind: "NO_CREDITS", at: dateOf(org.findPersonAttemptedAt) };
	}

	return { kind: "NONE" };
}

/** The row shows the person the tracker's automatic search found for its company. */
export function isFoundAutomatically(row: ListPerson): boolean {
	const foundId = trackerOrganizationOf(row)?.findPersonProspectId;

	return (
		row.list?.sourceType === ListSourceType.TRACKER &&
		!!row.prospect?.id &&
		!!foundId &&
		foundId === row.prospect.id
	);
}
